/**
 * Optional gridding request fields (pre-processing and breaklines) built from the
 * AnalysisPanel form state. Everything is off unless switched on, so a default request
 * is exactly what it was before these options existed.
 */

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : NaN;
};

/**
 * @param {object} params - AnalysisPanel params (ppDedupe, ppDedupeTol, ppDedupeStat,
 *   ppOutlier, ppOutlierMethod, ppOutlierK, ppDecluster, ppDeclusterCell)
 * @returns {{ fields: object }} request fields to merge into the gridding body
 * @throws Error with a user-facing message for an enabled option with a bad value
 */
export function buildPreprocessFields(params = {}) {
  const fields = {};
  if (params.ppDedupe) {
    const tolerance = num(params.ppDedupeTol);
    if (!(tolerance > 0)) throw new Error('Duplicate merge: enter a tolerance > 0 (degrees)');
    fields.dedupe = { tolerance, statistic: params.ppDedupeStat === 'median' ? 'median' : 'mean' };
  }
  if (params.ppOutlier) {
    const method = params.ppOutlierMethod === 'iqr' ? 'iqr' : 'zscore';
    const k = params.ppOutlierK === undefined || params.ppOutlierK === '' ? (method === 'iqr' ? 1.5 : 3) : num(params.ppOutlierK);
    if (!(k > 0)) throw new Error('Outlier filter: k must be > 0');
    fields.outlier_filter = { method, k };
  }
  if (params.ppDecluster) {
    const cell = num(params.ppDeclusterCell);
    if (!(cell > 0)) throw new Error('Declustering: enter a cell size > 0 (degrees)');
    fields.declustering = { cell_size: cell };
  }
  return fields;
}

export const MAX_BREAKLINE_SEGMENTS = 500;

/**
 * Breaklines from drawn polylines (Terra Draw features): LineString and MultiLineString
 * parts with >= 2 vertices, as [[lon, lat], ...] arrays.
 * @returns {{ breaklines?: number[][][] }} empty unless `params.ppBreaklines` is on
 * @throws Error when enabled with no drawn line or too many segments
 */
export function buildBreaklineFields(params = {}, drawnFeatures = []) {
  if (!params.ppBreaklines) return {};
  const lines = [];
  for (const f of drawnFeatures) {
    const g = f?.geometry;
    if (g?.type === 'LineString') lines.push(g.coordinates);
    else if (g?.type === 'MultiLineString') lines.push(...g.coordinates);
  }
  const clean = lines
    .map((l) => l.map((c) => [c[0], c[1]]))
    .filter((l) => l.length >= 2 && l.every((c) => Number.isFinite(c[0]) && Number.isFinite(c[1])));
  if (clean.length === 0) throw new Error('Breaklines: draw at least one polyline on the map first');
  const segments = clean.reduce((s, l) => s + l.length - 1, 0);
  if (segments > MAX_BREAKLINE_SEGMENTS) throw new Error(`Breaklines: at most ${MAX_BREAKLINE_SEGMENTS} segments (drawn ${segments})`);
  return { breaklines: clean };
}

/** One-line "n points removed / merged" summary of `metadata.preprocess`; '' when none was applied. */
export function summarizePreprocess(info) {
  if (!info || !info.n_input) return '';
  const parts = [];
  if (info.n_outliers_removed !== undefined) parts.push(`${info.n_outliers_removed} outlier${info.n_outliers_removed === 1 ? '' : 's'} removed`);
  if (info.n_merged !== undefined) parts.push(`${info.n_merged} merged`);
  if (info.declustered) parts.push(`declustered (${info.n_cells} cells)`);
  return `${parts.join(', ')} — ${info.n_used} of ${info.n_input} points used`;
}
